/**
 * DB helper functions needed by the document builders.
 * Extracted from backend/service/core/src/functions/utils/dbHelpers.ts
 * Only the functions used by save-*-to-search builders are included here.
 */
import { DbTransaction } from "@tc/middy-decorators"
import { transferRequest, refCountry, templateRevision } from "@tc/drizzle-v2"
import { eq, inArray } from "drizzle-orm"
import { TransferRequestRow } from "../types/core"

export const getTransferRequest =
  (tx: DbTransaction) =>
  async (transferRequestId: string): Promise<TransferRequestRow> => {
    const [row] = await tx
      .select()
      .from(transferRequest)
      .where(eq(transferRequest.id, transferRequestId))

    if (!row) {
      throw new Error(
        `Transfer request with ID ${transferRequestId} not found.`,
      )
    }

    return row
  }

export const getCountryInfoByCode = async (
  tx: DbTransaction,
  countryCode: string,
) => {
  return await tx
    .select()
    .from(refCountry)
    .where(eq(refCountry.itemCode, countryCode))
}

/** Batch fetch country info. Eagerly loads ALL countries on first call — table is tiny (~249 rows). */
let countryFullyCached = false
const countryCache = new Map<string, string>()
export const getCountryInfoBatch = async (
  tx: DbTransaction,
  countryCodes: readonly string[],
): Promise<Map<string, string>> => {
  if (countryCodes.length === 0) return new Map()
  if (!countryFullyCached) {
    const rows = await tx
      .select({ itemCode: refCountry.itemCode, itemName: refCountry.itemName })
      .from(refCountry)
    for (const row of rows) {
      if (row.itemCode && row.itemName) countryCache.set(row.itemCode, row.itemName)
    }
    countryFullyCached = true
  }
  const result = new Map<string, string>()
  for (const code of countryCodes) {
    const name = countryCache.get(code)
    if (name) result.set(code, name)
  }
  return result
}

export const getTemplateInfoByVersionId = async (
  tx: DbTransaction,
  templateVersionId: string,
): Promise<{ templateId: string | null; submissionLayout: string | null }> => {
  const [result] = await tx
    .select({
      templateId: templateRevision.templateId,
      submissionLayout: templateRevision.submissionLayout,
    })
    .from(templateRevision)
    .where(eq(templateRevision.id, templateVersionId))
  return {
    templateId: result?.templateId ?? null,
    submissionLayout: result?.submissionLayout ?? null,
  }
}

/** Batch fetch template info. Eagerly loads ALL template revisions on first call. */
let templateInfoFullyCached = false
const templateInfoCache = new Map<string, { templateId: string | null; submissionLayout: string | null }>()
export const getTemplateInfoBatch = async (
  tx: DbTransaction,
  templateVersionIds: readonly string[],
): Promise<Map<string, { templateId: string | null; submissionLayout: string | null }>> => {
  if (templateVersionIds.length === 0) return new Map()
  if (!templateInfoFullyCached) {
    const rows = await tx
      .select({
        id: templateRevision.id,
        templateId: templateRevision.templateId,
        submissionLayout: templateRevision.submissionLayout,
      })
      .from(templateRevision)
    for (const row of rows) {
      templateInfoCache.set(row.id, { templateId: row.templateId, submissionLayout: row.submissionLayout })
    }
    templateInfoFullyCached = true
  }
  const result = new Map<string, { templateId: string | null; submissionLayout: string | null }>()
  for (const id of templateVersionIds) {
    const info = templateInfoCache.get(id)
    if (info) result.set(id, info)
  }
  return result
}
